import React, { useEffect, useState } from "react";
import { deburr, filter, includes, map, toLower } from "lodash";
import styled from "styled-components";
import {
  FontSize,
  FontWeight,
  Spacing,
  content,
  dominant,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import Button from "@ui-kit/button";
import { Text } from "@ui-kit/form";
import Icon from "@ui-kit/icon";
import Tooltip from "@ui-kit/tooltip";
import { tr } from "@radarly/i18n";

type Value = {
  value: string;
  label?: string;
  compatible?: boolean;
};

export interface IProps {
  title: string;
  searchPlaceholder: string;
  sections: {
    title?: string;
    values: Value[];
  }[];
  selected?: Value["value"];
  onSelect: (value: Value["value"]) => void;
  onClickBack: () => void;
  locale: Locale;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  flex: 0;
  display: flex;
  align-items: center;
  padding: ${Spacing.Large} ${Spacing.Medium} 0;
`;

const Search = styled.div`
  padding: ${Spacing.ExtraSmall} ${Spacing.Medium};
  border-bottom: 1px solid ${(props) => content(props, "tertiary")};
`;

const Title = styled.span`
  margin-left: ${Spacing.ExtraSmall};
  color: ${content};
  font-size: ${FontSize.Large};
  font-weight: ${FontWeight.Bold};
`;

const Body = styled.div`
  padding: ${Spacing.Large} 0;
  overflow: auto;
  line-height: 32px;
`;

const Section = styled.div<{ isSearching: boolean }>`
  &:not(:first-child) {
    margin-top: ${(props) => !props.isSearching && Spacing.Medium};
  }
`;

const Label = styled.div`
  padding: 0 ${Spacing.Large};
  font-weight: ${FontWeight.Bold};
  color: ${content};
`;

const Item = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 0 ${Spacing.Large};
  color: ${(props) =>
    props.selected ? dominant(props) : content(props, "secondary")};
  background-color: ${(props) =>
    props.selected && transparentize(0.8, dominant(props))};
  :hover {
    background-color: ${(props) =>
      !props.selected && transparentize(0.85, content(props, "secondary"))};
  }
`;

const PanelsCardEditionAttributeSelector = ({
  title,
  searchPlaceholder,
  sections,
  selected,
  onSelect,
  onClickBack,
  locale,
}: IProps) => {
  const [search, setSearch] = useState("");
  const [filteredSections, setFilteredSections] = useState(sections);

  useEffect(() => {
    setFilteredSections(
      map(sections, (section) => ({
        ...section,
        values: filter(section.values, (v) =>
          includes(toLower(deburr(v.label)), toLower(deburr(search)))
        ),
      }))
    );
  }, [search, sections]);

  return (
    <Root>
      <Header>
        <Button icon="arrow-left" variant="tertiary" onClick={onClickBack} />
        <Title>{title}</Title>
      </Header>
      <Search>
        <Text
          isClearable
          onChange={(e) => setSearch(e)}
          placeholder={searchPlaceholder}
          value={search}
          variant="primary"
        />
      </Search>
      <Body>
        {map(filteredSections, ({ title, values }, k) => (
          <Section key={k} isSearching={!!search}>
            {!search && <Label>{title}</Label>}
            {!!values &&
              map(values, (item, i) => (
                <Item
                  key={i}
                  onClick={() => onSelect(item.value)}
                  selected={item.value === selected}
                >
                  {item.label}
                  {item.compatible && (
                    <Tooltip
                      placement="left"
                      tooltip={tr(
                        locale,
                        "CUSTOM_PAGES::EDITOR::COMPATIBLE_ATTRIBUTE"
                      )}
                    >
                      <div>
                        <Icon size={12} glyph="check" />
                      </div>
                    </Tooltip>
                  )}
                </Item>
              ))}
          </Section>
        ))}
      </Body>
    </Root>
  );
};

export default PanelsCardEditionAttributeSelector;
